import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedSmartId } from "../../../../redux/Slices/smartAdSchedulerSlice";
import { useGetSmartAdLogs } from "../../../../queries/smartAdScheduler";
import { Button } from "../../../../components/ui/Button";
import { ProgressSpinner } from "primereact/progressspinner";

const PHASE_ICONS = {
    pre_match:   { icon: "pi pi-clock",       cls: "text-blue-500"   },
    first_half:  { icon: "pi pi-play",         cls: "text-green-500"  },
    halftime:    { icon: "pi pi-pause",        cls: "text-yellow-600" },
    second_half: { icon: "pi pi-play",         cls: "text-green-500"  },
    extra_time:  { icon: "pi pi-bolt",         cls: "text-orange-500" },
    fulltime:    { icon: "pi pi-check-circle", cls: "text-gray-500"   },
    cancelled:   { icon: "pi pi-times-circle", cls: "text-red-500"    },
};

const buildPhaseLabels = (t) => ({
    pre_match: t("smart_ads.phase_pre_match"), first_half: t("smart_ads.phase_first_half"),
    halftime: t("smart_ads.phase_halftime"), second_half: t("smart_ads.phase_second_half"),
    extra_time: t("smart_ads.phase_extra_time"), fulltime: t("smart_ads.phase_fulltime"),
    cancelled: t("status.cancelled"),
});

export default function SmartAdLogsPanel() {
    const { t }           = useTranslation();
    const dispatch        = useDispatch();
    const selectedSmartId = useSelector((s) => s.smartAdScheduler.selectedSmartId);
    const [page, setPage]  = useState(1);

    const PHASE_LABELS = buildPhaseLabels(t);

    const { data, isLoading } = useGetSmartAdLogs(selectedSmartId, page);
    const logs       = data?.data ?? [];
    const pagination = data?.pagination ?? {};

    if (!selectedSmartId) return null;

    return (
        <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl border-l z-50 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
                <div>
                    <h3 className="font-semibold text-gray-800 text-sm">{t("smart_ads.logs_title")}</h3>
                    <p className="text-xs text-gray-400">{t("smart_ads.logs_subtitle")}</p>
                </div>
                <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-sm p-button-rounded"
                    onClick={() => { dispatch(setSelectedSmartId(null)); setPage(1); }}
                />
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4">
                {isLoading ? (
                    <div className="flex justify-center py-12"><ProgressSpinner /></div>
                ) : logs.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        <i className="pi pi-history text-3xl mb-2 block" />
                        <p className="text-sm">{t("smart_ads.no_logs")}</p>
                    </div>
                ) : (
                    <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-3.5 top-2 bottom-0 w-px bg-gray-200" />

                        <div className="space-y-4">
                            {logs.map((log) => {
                                const phaseInfo = PHASE_ICONS[log.to_phase] ?? { icon: "pi pi-circle", cls: "text-gray-400" };
                                return (
                                    <div key={log.id} className="relative flex gap-3 pl-1">
                                        {/* Phase icon */}
                                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center z-10">
                                            <i className={`${phaseInfo.icon} ${phaseInfo.cls} text-xs`} />
                                        </div>

                                        {/* Log content */}
                                        <div className="flex-1 bg-gray-50 rounded-lg p-2.5 text-xs">
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-1.5">
                                                    {log.from_phase && (
                                                        <>
                                                            <span className="text-gray-400">{PHASE_LABELS[log.from_phase] ?? log.from_phase}</span>
                                                            <i className="pi pi-arrow-right text-gray-300" style={{ fontSize: "0.6rem" }} />
                                                        </>
                                                    )}
                                                    <span className="font-semibold text-gray-700">
                                                        {PHASE_LABELS[log.to_phase] ?? log.to_phase}
                                                    </span>
                                                </div>
                                                {log.push_sent ? (
                                                    <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">{t("smart_ads.log_pushed")}</span>
                                                ) : (
                                                    <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">{t("smart_ads.log_no_push")}</span>
                                                )}
                                            </div>

                                            {log.ad_campaign && (
                                                <p className="text-gray-600 truncate">
                                                    <i className="pi pi-ad mr-1 text-gray-400" />
                                                    {log.ad_campaign.name}
                                                    <span className="ml-1 text-gray-400 capitalize">
                                                        ({log.ad_campaign.ad_type?.replace("_", " ")})
                                                    </span>
                                                </p>
                                            )}

                                            <div className="flex items-center justify-between mt-1 text-gray-400">
                                                <span>{t("smart_ads.devices_notified", { count: log.devices_notified })}</span>
                                                <span>{log.api_status && `[${log.api_status}]`}</span>
                                            </div>

                                            {log.error_message && (
                                                <p className="text-red-500 mt-1 text-xs">{log.error_message}</p>
                                            )}

                                            <p className="text-gray-300 mt-1">
                                                {log.occurred_at
                                                    ? new Date(log.occurred_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })
                                                    : "—"}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
                <div className="border-t p-3 flex items-center justify-between">
                    <Button
                        icon="pi pi-chevron-left"
                        className="p-button-text p-button-sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                    />
                    <span className="text-xs text-gray-500">
                        {t("ad_marketplace.page_of", { current: page, total: pagination.total_pages })}
                    </span>
                    <Button
                        icon="pi pi-chevron-right"
                        className="p-button-text p-button-sm"
                        disabled={page >= pagination.total_pages}
                        onClick={() => setPage((p) => p + 1)}
                    />
                </div>
            )}
        </div>
    );
}
